<template>
	<div class="uploaders-container">
		<fake-uploader
        v-for="params in tempParams"
        :key="params.uploaderId"
        :uploaderIdentifier="params.uploaderId"
        :openCameraLayer="params.openCameraLayer"
        :mandatory="params.mandatory"
        :errorState="params.errorState"
        :errorMessage="params.errorMessage"
        :label="params.label"
        :galleryOptionsTitle="params.galleryOptionsTitle"
        :importButtonLabel="params.importButtonLabel"
        :cameraButtonLabel="params.cameraButtonLabel"
        :cameraTitle="params.cameraTitle"
        :cameraMessage="params.cameraMessage"
        :cameraPreviewMessage="params.cameraPreviewMessage"
        :placeholder="params.placeholder"
        :fileNote="params.fileNote"
        :fileName="params.fileName"
        :removeFile="params.removeFile"
        :uploadedFile="params.file"
        :removeLoader="params.removeLoader"
        @fileRemoved="fileRemovedHandler"
        @fileAdded="fileAddedHandler"/>
    </div>
    <!-- Feedback -->
    <toast-generic
    v-if="showFeedbackToast"
    :toastType="feedbackToastData.toastType"
    :feedbackText="feedbackToastData.text"
    :buttonsParams="feedbackToastData.buttons"
    @hideMe="showFeedbackToast = false"/>
    <!-- Loader Generic -->
    <loaders-generic v-if="showLoader" isOverlay/>
</template>

<script setup>
import FakeUploader from '@/components/generic/uploader/multipleUploader/FakeUploader'
import ToastGeneric from '@/components/generic/toasts/Generic'
import LoadersGeneric from '@/components/generic/loaders/Generic'

/* Vue */
import { ref, onMounted } from 'vue'
import { errorObjectConstructor, sucessObjectConstrutor } from '@/assets/scripts/toastsHelpers'

/* Store */
import store from '@/store/store'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

const emit = defineEmits([
    'fileAdded',
    'fileRemoved'
])

/** Props received:
@uploaderParams {Array}: each item is an Object
    - @errorMessage {String} String used for Uploader Error Message
    - @errorState {Boolean} Boolean to apply Uploader Error State
    - @mandatory {Boolean}  Boolean to * on label
    - @label {String} String used for Uploader Label
    - @placeholder {String} String used for Uploader Placeholder
    - @galleryOptionsTitle {String} String used for gallery-camera-buttons title
    - @importButtonLabel {String} String used for gallery-camera-buttons import from gallery button label
    - @cameraButtonLabel {String} String used for gallery-camera-buttons use device camera button label
    - @cameraTitle {String} String used in camera layer header
    - @cameraMessage {String} String used in camera layer message above camera lens
    - @cameraPreviewMessage {String} String used in preview layer message above image preview
    - @fileNote {String} String used in uploader bottom message
    - @uploaderIdentifier {String} String used to identify the uploader when there is more than 1 at the same time
    - @uploadedFile {Object, File} Uploaded file
    - @removeFile {Boolean} Boolean to trigger remove file handling
    - @fileName {String} String used on uploaded file name
    - @removeLoader {Boolean} Boolean to trigger remove loader and Preview Layer
    - @openCameraLayer {Boolean} Boolean to trigger camera layer to open
@uploadAction {String} Action for upload files request
@deleteAction {String} Action for deleting uploaded files
*/
const props = defineProps({
    uploaderParams: Array,
    uploadAction: String,
    deleteAction: String

})

const showLoader = ref(false)
const tempParams = ref([])

const fileAddedHandler = async event => {
    tempParams.value.find(param => param.uploaderId === event.id).removeLoader = false

    const selectedUploader = tempParams.value.find(param => param.uploaderId === event.id)

    const payload = {
        params: selectedUploader.uploadPayload,
        data: buildFormData({ blob: event.blob, fileName: selectedUploader.fileName })
    }

    try {
        if (props.uploadAction && payload) {
            window.cordova.plugin.http.setDataSerializer('multipart')
            const response = await store.dispatch(props.uploadAction, payload)
            window.cordova.plugin.http.setDataSerializer('json')
            successHandler(response.data)
        }

        emit('fileAdded', event)

        tempParams.value.find(param => param.uploaderId === event.id).file = event.file
    } catch (error) {
        errorHandler(error.response.data)
    } finally {
        tempParams.value.find(param => param.uploaderId === event.id).removeLoader = true
    }
}

const fileRemovedHandler = async event => {
    showLoader.value = true
    const selectedUploader = tempParams.value.find(param => param.uploaderId === event.id)

    const payload = {
        params: selectedUploader.uploadPayload
    }

    try {
        if (props.deleteAction && payload) {
            const response = await store.dispatch(props.deleteAction, payload)
            successHandler(response.data)
        }

        emit('fileRemoved', event)
        tempParams.value.find(param => param.uploaderId === event.id).file = event.file
    } catch (error) {
        errorHandler(error.response.data)
    } finally {
        showLoader.value = false
    }
}

const buildFormData = ({ blob, fileName }) => {
    const form = new FormData()

    /* Blob Info */
    form.append('file', blob, `${fileName}.jpg`)

    return form
}

onMounted(() => {
    tempParams.value = props.uploaderParams

    tempParams.value.forEach(param => {
        param.openCameraLayer = false
        param.removeLoader = false
    })
})

/*************************************************
*                                                *
*                 Toast Handler                  *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)
const feedbackToastData = ref({
	toastType: '',
	text: '',
	buttons: []
})

const successHandler = (data) => {
	feedbackToastData.value = sucessObjectConstrutor(data.message)
	showFeedbackToast.value = true
}

const errorHandler = data => {
    feedbackToastData.value = errorObjectConstructor(data)
    showFeedbackToast.value = true
}
</script>

<style lang="scss" scoped>
.uploaders-container {
	width:100%;
    max-width: 508px;

	display:flex;
	flex-direction:column;
	position:relative;

    gap: 32px;

}
</style>
